<template>
  <UiContainer class="cart-view">
    <div class="cart-view__header">
      <h1 class="cart-view__title">Корзина</h1>
      <p class="cart-view__subtitle">
        {{
          isEmpty
            ? 'Добавьте товары из каталога'
            : `${items.length} ${getItemsWord(items.length)} на сумму ${formatTotalPrice()}`
        }}
      </p>
    </div>

    <div v-if="isEmpty" class="cart-view__empty">
      <span class="cart-view__empty-icon">🛒</span>
      <h3 class="cart-view__empty-heading">Корзина пуста</h3>
      <UiButton to="/catalog">Перейти к покупкам</UiButton>
    </div>

    <div v-else class="cart-view__grid">
      <UiCard class="cart-view__list">
        <h2 class="cart-view__card-title">Товары в корзине</h2>
        <div v-for="item in items" :key="item.productId" class="cart-view__row">
          <img
            :src="item.image || '/placeholder-product.svg'"
            :alt="item.name"
            class="cart-view__row-img"
            width="80"
            height="80"
          />
          <div class="cart-view__row-info">
            <h4 class="cart-view__row-name">{{ item.name }}</h4>
            <p class="cart-view__row-sku">Артикул: {{ item.sku || item.productId }}</p>
          </div>
          <div class="cart-view__row-actions">
            <div class="cart-view__row-qty">
              <UiButton
                variant="secondary"
                type="button"
                class="cart-view__qty-btn"
                aria-label="Уменьшить"
                :disabled="item.quantity <= (item.minimumOrder ?? 1)"
                @click="updateQuantity(item.productId, item.quantity - 1)"
              >
                −
              </UiButton>
              <span class="cart-view__qty-value">{{ item.quantity }}</span>
              <UiButton
                variant="secondary"
                type="button"
                class="cart-view__qty-btn"
                aria-label="Увеличить"
                @click="updateQuantity(item.productId, item.quantity + 1)"
              >
                +
              </UiButton>
            </div>
            <div class="cart-view__row-total">
              {{ formatPriceWithCents(item.totalPrice ?? item.price * item.quantity) }}
            </div>
            <UiButton
              variant="secondary"
              type="button"
              class="cart-view__row-remove"
              aria-label="Удалить"
              @click="removeItem(item.productId)"
            >
              ✕
            </UiButton>
          </div>
        </div>
      </UiCard>

      <UiCard class="cart-view__summary">
        <h2 class="cart-view__card-title">Итого</h2>
        <div class="cart-view__summary-row">
          <span>Товаров:</span>
          <span>{{ itemsCount }}</span>
        </div>
        <div class="cart-view__summary-row cart-view__summary-row--total">
          <span>Сумма:</span>
          <span>{{ formatPriceWithCents(totalAmount) }}</span>
        </div>
        <UiButton block to="/checkout" class="cart-view__checkout-btn"> Оформить заказ </UiButton>
      </UiCard>
    </div>
  </UiContainer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import { useCartWorkspace } from '@/modules/order/public'
import { useAppNotifications } from '@/modules/common/public'
import { formatPriceWithCents } from '@/utils/price'

const cartStore = useCartWorkspace()
const notificationStore = useAppNotifications()

const items = computed(() => cartStore.items.value)
const isEmpty = computed(() => cartStore.isEmpty.value)
const itemsCount = computed(() => cartStore.itemsCount.value)
const totalAmount = computed(() => cartStore.totalAmount.value)

async function updateQuantity(productId: string | number, quantity: number) {
  try {
    await cartStore.updateQuantity(productId, quantity)
  } catch (error) {
    notificationStore.showError(
      error instanceof Error ? error.message : 'Не удалось обновить количество',
    )
  }
}

async function removeItem(productId: string | number) {
  try {
    await cartStore.removeItem(productId)
  } catch (error) {
    notificationStore.showError(error instanceof Error ? error.message : 'Не удалось удалить товар')
  }
}

function getItemsWord(n: number): string {
  const last2 = n % 100
  const last1 = n % 10
  if (last2 >= 11 && last2 <= 14) return 'товаров'
  if (last1 === 1) return 'товар'
  if (last1 >= 2 && last1 <= 4) return 'товара'
  return 'товаров'
}

function formatTotalPrice(): string {
  return formatPriceWithCents(totalAmount.value)
}
</script>

<style scoped>
.cart-view {
  padding-top: 24px;
  padding-bottom: 48px;
}

.cart-view__header {
  margin-bottom: 24px;
}

.cart-view__title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
}

.cart-view__subtitle {
  margin: 0;
  color: #586578;
  font-size: 14px;
}

.cart-view__empty {
  text-align: center;
  padding: 48px 16px;
}

.cart-view__empty-icon {
  font-size: 64px;
  display: block;
  margin-bottom: 16px;
  opacity: 0.6;
}

.cart-view__empty-heading {
  margin: 0 0 24px;
  font-size: 18px;
  font-weight: 600;
}

.cart-view__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;
}

@media (max-width: 960px) {
  .cart-view__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cart-view__list,
.cart-view__summary {
  padding: 20px;
}

.cart-view__card-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
}

.cart-view__row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #e2e8f0;
}

.cart-view__row:last-child {
  border-bottom: none;
}

.cart-view__row-img {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  background: #f1f5f9;
}

.cart-view__row-info {
  flex: 1;
  min-width: 0;
}

.cart-view__row-name {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
}

.cart-view__row-sku {
  margin: 0;
  font-size: 13px;
  color: #586578;
}

.cart-view__row-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.cart-view__row-qty {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cart-view__qty-btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  padding: 0;
}

.cart-view__qty-value {
  min-width: 24px;
  text-align: center;
  font-weight: 600;
}

.cart-view__row-total {
  font-weight: 600;
  width: 150px;
  flex-shrink: 0;
  text-align: right;
}

@media (max-width: 640px) {
  .cart-view__row {
    flex-wrap: wrap;
  }

  .cart-view__row-actions {
    flex: 1 1 100%;
    flex-shrink: 1;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .cart-view__row-total {
    width: auto;
    flex: 1;
  }
}

.cart-view__row-remove {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  padding: 0;
}

.cart-view__summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cart-view__summary-row--total {
  margin-top: 12px;
  margin-bottom: 20px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  font-weight: 600;
  font-size: 18px;
}

.cart-view__checkout-btn {
  margin-top: 0;
}
</style>
